import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { BrandingContext } from '../../../../BrandingProvider';
import { Box, Grid, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Typography, Chip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip, { invChipSx } from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DetailRow } from '../../components/InvField';
import { getGrn, getGrnItems, cancelGrn } from '../../api/grn';
import { getVendors } from '../../api/vendor';
import { getStores } from '../../api/stockLedger';
import { getPurchaseOrder } from '../../api/procurement';
import { getVendorReturns } from '../../api/vendorReturn';
import { formatDate, formatCurrency, formatQty, lineTotal, computeGrnFinancials } from '../../utils';
import useItemNames from '../../hooks/useItemNames';
import { buildGrnPdfPackage, openGrnPdfUrl, downloadGrnPdfUrl } from '../../utils/grnPdf';
import LetterheadMenu, { useLetterheadMenu } from '../../components/LetterheadMenu';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { canCancelGrn, getGrnCancelBlockReason } from '../../utils/grnLifecycle';
import { IPD_COLORS } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

/** Show detail rows only when the user actually entered a value. */
function hasValue(value) {
  if (value == null || value === '') return false;
  return String(value).trim() !== '';
}

const GRN_KIND_COLORS = {
  REPLACEMENT: { bg: IPD_COLORS.info.text, text: '#fff' },
  RETURN:      { bg: IPD_COLORS.warning.text, text: '#fff' },
  DIRECT:      { bg: IPD_COLORS.textPrimary, text: '#fff' },
  STANDARD:    { bg: IPD_COLORS.primary, text: '#fff' },
};

function GrnKindChip({ kind }) {
  if (!kind) return <>—</>;
  const c = GRN_KIND_COLORS[kind] || { bg: IPD_COLORS.textPrimary, text: '#fff' };
  return (
    <Chip
      label={kind.replace(/_/g, ' ')}
      size="small"
      sx={invChipSx({ color: c.bg, onColor: c.text })}
    />
  );
}

/** Best-effort — only renders once the backend actually exposes the source linkage. */
function replacementSourceGrnNumber(grn) {
  return grn?.replacement_for_grn_number
    || grn?.source_grn_number
    || grn?.grn_payload?.replacement_for_grn_number
    || grn?.grn_payload?.source_grn_number
    || null;
}

function replacementSourceGrnId(grn) {
  return grn?.replacement_for_grn_id
    || grn?.source_grn_id
    || grn?.grn_payload?.replacement_for_grn_id
    || grn?.grn_payload?.source_grn_id
    || null;
}

export default function GrnDetail() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const { resolveItem } = useItemNames();
  const base = url.replace(/\/grn\/.*/, '');
  const brandingData = useContext(BrandingContext);
  const facilityLogo = (() => {
    const img = brandingData?.find(b => b.name === 'collateral_image')?.content;
    return img ? `data:image/png;base64,${img}` : null;
  })();
  const [grn, setGrn] = useState(null);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [stores, setStores] = useState([]);
  const [poNumber, setPoNumber] = useState(null);
  const [vendorReturns, setVendorReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const letterheadMenu = useLetterheadMenu();
  const [pdfPackage, setPdfPackage] = useState(null);
  const pdfUrlRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      // API: GET /inventory/grns/{id}/
      const g = await getGrn(id);
      setGrn(g);
      const scope = { tenant_id: g.tenant_id, hospital_id: g.hospital_id };
      const [itemsData, vendorsData, storesData] = await Promise.all([
        // API: GET /inventory/grn-items/ — filter by grn_id
        getGrnItems(id, scope).catch(() => []),
        // API: GET /inventory/vendors/
        getVendors().catch(() => []),
        // API: GET /inventory/stores/
        getStores().catch(() => []),
      ]);
      setItems(normalizeList(itemsData));
      setVendors(normalizeList(vendorsData));
      setStores(normalizeList(storesData));

      const poId = g.purchase_order_id;
      if (poId) {
        // API: GET /inventory/purchase-orders/{id}/
        getPurchaseOrder(poId).then(p => setPoNumber(p.po_number)).catch(() => {});
      }

      // API: GET /inventory/vendor-returns/ — filter by source_grn_id
      getVendorReturns(scope, { source_grn_id: id })
        .then(d => setVendorReturns(normalizeList(d)))
        .catch(() => setVendorReturns([]));
    } catch { /* handled by empty state */ } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]);

  function resolveVendorName(vendorId) {
    if (!vendorId) return null;
    const v = vendors.find(x => (x.vendor_id || x.id) === vendorId);
    if (!v) return vendorId;
    return v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name;
  }

  function resolveStoreName(storeId) {
    if (!storeId) return null;
    const s = stores.find(x => (x.store_id || x.id) === storeId);
    if (!s) return storeId;
    return `${s.store_name} (${s.store_code})`;
  }

  function pdfExtras(letterhead) {
    return {
      vendorName:  resolveVendorName(grn?.vendor_id),
      storeName:   resolveStoreName(grn?.receiving_store_id),
      poNumber:    poNumber || grn?.po_number,
      facilityLogo,
      letterhead,
    };
  }

  const revokePdfUrl = useCallback(() => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
  }, []);

  const handleGeneratePdf = useCallback(async (letterhead) => {
    if (!grn) return;
    setPdfLoading(true);
    try {
      revokePdfUrl();
      const scope = { tenant_id: grn.tenant_id, hospital_id: grn.hospital_id };
      const pkg = await buildGrnPdfPackage(id, scope, pdfExtras(letterhead));
      pdfUrlRef.current = pkg.url;
      setPdfPackage(pkg);
    } catch (e) {
      toast.error(e.message || 'Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  }, [grn, id, poNumber, vendors, stores, revokePdfUrl]);

  useEffect(() => () => revokePdfUrl(), [revokePdfUrl]);

  function handleViewPdf() {
    if (!pdfPackage?.url) {
      toast.info('Generate the PDF first');
      return;
    }
    openGrnPdfUrl(pdfPackage.url);
  }

  function handleDownloadPdf() {
    if (!pdfPackage?.url) {
      toast.info('Generate the PDF first');
      return;
    }
    downloadGrnPdfUrl(pdfPackage.url, pdfPackage.filename);
  }

  async function handleCancelGrn() {
    // Re-check here too: the status may have moved on since this page was rendered.
    const blocked = getGrnCancelBlockReason(grn);
    if (blocked) {
      toast.error(blocked);
      setCancelDialogOpen(false);
      return;
    }
    setCancelling(true);
    try {
      // API: DELETE /inventory/grns/{id}/ — soft-delete, sets status=CANCELLED
      await cancelGrn(id);
      setCancelDialogOpen(false);
      toast.success('GRN cancelled');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to cancel GRN');
    } finally {
      setCancelling(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (!grn) return null;

  const displayStatus = grn.posting_status || grn.status;
  const financials = computeGrnFinancials(items, { useAccepted: true });
  const taxEntries = Object.entries(financials.taxBuckets).sort(([a], [b]) => Number(a) - Number(b));
  const rejectedTotal = items.reduce((sum, it) => {
    const received = Math.floor(Number(it.received_quantity) || 0);
    const accepted = Math.floor(Number(it.accepted_quantity) || 0);
    return sum + (it.rejected_quantity != null ? Math.floor(Number(it.rejected_quantity) || 0) : Math.max(0, received - accepted));
  }, 0);
  const canCreateReturn = displayStatus === 'POSTED_TO_STOCK' && rejectedTotal > 0;
  // Cancel and delete are the same server operation (soft-delete sets status=CANCELLED).
  // An already-cancelled GRN has nothing left to offer, so the action is dropped entirely
  // rather than shown disabled — every other blocked state keeps the button and explains itself.
  const showCancelGrn = can(INV_ACTIONS.GRN_CREATE) && canCancelGrn(grn);

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › Goods Receipt Notes"
        title={grn.grn_number || 'GRN'}
        actions={
          <>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.push(`${base}/grns`)}>
              Back to List
            </InvButton>
            <InvButton startIcon={<PictureAsPdfIcon />} size="small" loading={pdfLoading}
              onClick={(e) => letterheadMenu.open(e, handleGeneratePdf)}>
              {pdfPackage ? 'Regenerate PDF' : 'Generate PDF'}
            </InvButton>
            <LetterheadMenu menu={letterheadMenu} label="Goods Receipt Note" />
            <InvButton startIcon={<VisibilityIcon />} size="small" variant="outlined" tone="teal"
              disabled={!pdfPackage || pdfLoading} onClick={handleViewPdf}>
              View PDF
            </InvButton>
            <InvButton startIcon={<DownloadIcon />} size="small" variant="outlined" tone="mint"
              disabled={!pdfPackage || pdfLoading} onClick={handleDownloadPdf}>
              Download PDF
            </InvButton>
            {canCreateReturn && can(INV_ACTIONS.GRN_CREATE_VENDOR_RETURN) && (
              <InvButton startIcon={<AssignmentReturnIcon />} size="small" tone="red" variant="outlined"
                onClick={() => history.push(`${base}/vendor-return/new?grn_id=${id}`)}>
                Create Vendor Return
              </InvButton>
            )}
            {displayStatus === 'POSTED_TO_STOCK' && can(INV_ACTIONS.GRN_CREATE_INVOICE) && (
              <InvButton startIcon={<ReceiptLongIcon />} size="small" tone="teal" variant="outlined"
                onClick={() => history.push(`${base}/purchase-invoice/new?grn_id=${id}`)}>
                Create Purchase Invoice
              </InvButton>
            )}
            {showCancelGrn && (
              <InvButton startIcon={<DeleteOutlineIcon />} size="small" tone="red" variant="outlined"
                onClick={() => setCancelDialogOpen(true)}>
                Cancel GRN
              </InvButton>
            )}
          </>
        }
      />

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <SectionCard title="GRN Details" sx={{ height: '100%' }} actions={<StatusChip status={displayStatus} />}>
            <DetailRow label="GRN Date">{formatDate(grn.grn_date)}</DetailRow>
            <DetailRow label="GRN Kind"><GrnKindChip kind={grn.grn_kind} /></DetailRow>
            {replacementSourceGrnNumber(grn) && (
              <DetailRow label="Replacement For">
                {replacementSourceGrnId(grn) ? (
                  <Typography
                    onClick={() => history.push(`${base}/grn/${replacementSourceGrnId(grn)}`)}
                    sx={{ fontSize: 13, color: IPD_COLORS.primary, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                  >
                    GRN {replacementSourceGrnNumber(grn)}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>
                    GRN {replacementSourceGrnNumber(grn)}
                  </Typography>
                )}
              </DetailRow>
            )}
            <DetailRow label="PO">{poNumber || grn.purchase_order_id}</DetailRow>
            <DetailRow label="Vendor">{resolveVendorName(grn.vendor_id)}</DetailRow>
            <DetailRow label="Store">{resolveStoreName(grn.receiving_store_id)}</DetailRow>
            {hasValue(grn.invoice_number) && (
              <DetailRow label="Invoice">{grn.invoice_number}</DetailRow>
            )}
            {hasValue(grn.invoice_date) && (
              <DetailRow label="Invoice Date">{formatDate(grn.invoice_date)}</DetailRow>
            )}
            {hasValue(grn.payment_type) && (
              <DetailRow label="Payment Type">{grn.payment_type}</DetailRow>
            )}
            {hasValue(grn.place_of_supply) && (
              <DetailRow label="Place of Supply">{grn.place_of_supply}</DetailRow>
            )}
            {hasValue(grn.delivery_challan_number) && (
              <DetailRow label="Delivery Challan">{grn.delivery_challan_number}</DetailRow>
            )}
            {hasValue(grn.delivery_date) && (
              <DetailRow label="Delivery Date">{formatDate(grn.delivery_date)}</DetailRow>
            )}
            {hasValue(grn.external_po_reference) && (
              <DetailRow label="External PO Ref">{grn.external_po_reference}</DetailRow>
            )}
            {hasValue(grn.remarks) && (
              <DetailRow label="Remarks">{grn.remarks}</DetailRow>
            )}
            <DetailRow label="Created">{formatDate(grn.created_at)}</DetailRow>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <SectionCard title="Line Items" sx={{ height: '100%' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{
                minWidth: 620,
                '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary, py: 1.25 },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Ordered</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Received</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Accepted</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Expiry</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Rate</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(it => {
                    const qty = Math.floor(Number(it.accepted_quantity ?? it.received_quantity) || 0);
                    const rate = it.purchase_rate ?? it.unit_rate;
                    // Free (scheme) units are stocked but never billed, so the
                    // accepted quantity alone understates what reached the shelf
                    // and the invoice rate overstates what a unit cost.
                    const freeQty = Math.floor(Number(it.accepted_free_quantity) || 0);
                    const effectiveRate = it.effective_unit_cost;
                    const rateDiluted = freeQty > 0 && Number(effectiveRate) > 0;
                    return (
                    <TableRow key={it.grn_item_id || it.id}>
                      <TableCell>{resolveItem(it)}</TableCell>
                      <TableCell align="right">{formatQty(it.ordered_quantity)}</TableCell>
                      <TableCell align="right">{formatQty(it.received_quantity)}</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.success.text, fontWeight: 600 }}>
                        {formatQty(it.accepted_quantity ?? it.received_quantity)}
                        {freeQty > 0 && (
                          <Typography component="span" sx={{ display: 'block', fontSize: 11, fontWeight: 500, color: IPD_COLORS.textSecondary }}>
                            +{formatQty(freeQty)} free
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{it.batch_number || '—'}</TableCell>
                      <TableCell>{formatDate(it.expiry_date)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(rate)}
                        {rateDiluted && (
                          <Typography component="span" sx={{ display: 'block', fontSize: 11, fontWeight: 500, color: IPD_COLORS.textSecondary }}>
                            {formatCurrency(effectiveRate)} eff.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatCurrency(lineTotal(qty, rate, it.tax_percent, it.discount_percent))}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ color: IPD_COLORS.textSecondary, textAlign: 'center' }}>
                        No line items
                      </TableCell>
                    </TableRow>
                  )}
                  {items.length > 0 && (
                    <>
                      {taxEntries.map(([rate, amt]) => (
                        <TableRow key={`tax-${rate}`}>
                          <TableCell colSpan={7} align="right" sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>
                            GST ({rate}%)
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                            {formatCurrency(amt)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={7} align="right"
                          sx={{ fontWeight: 700, color: IPD_COLORS.primary, borderTop: `2px solid ${IPD_COLORS.border}` }}>
                          Grand Total
                        </TableCell>
                        <TableCell align="right"
                          sx={{ fontWeight: 700, color: IPD_COLORS.primary, borderTop: `2px solid ${IPD_COLORS.border}`, fontFamily: 'monospace' }}>
                          {formatCurrency(financials.grandTotal)}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {vendorReturns.length > 0 && (
        <SectionCard title="Vendor Returns" sx={{ mt: 2.5 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Return</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Type</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Date</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorReturns.map((r) => {
                  const returnId = r.vendor_return_id || r.id;
                  return (
                    <TableRow key={returnId}>
                      <TableCell>{r.return_number}</TableCell>
                      <TableCell>{r.return_type}</TableCell>
                      <TableCell><StatusChip status={r.status} /></TableCell>
                      <TableCell>{formatDate(r.return_date)}</TableCell>
                      <TableCell>
                        <InvButton size="small" variant="outlined" tone="teal"
                          onClick={() => history.push(`${base}/vendor-return/${returnId}`)}>
                          View
                        </InvButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </SectionCard>
      )}

      {pdfPackage?.url && !pdfLoading && (
        <SectionCard title={`GRN PDF Preview — ${pdfPackage.filename}`} sx={{ mt: 2.5 }}>
          <Box
            component="iframe"
            src={`${pdfPackage.url}#zoom=page-width`}
            title="GRN PDF Preview"
            sx={{ width: '100%', height: 560, border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1, display: 'block' }}
          />
        </SectionCard>
      )}

      <ConfirmDialog
        open={cancelDialogOpen}
        title="Cancel GRN"
        description={`Cancel ${grn.grn_number || 'this GRN'}? It will be marked CANCELLED and removed from active use. This cannot be undone.`}
        destructive
        loading={cancelling}
        confirmLabel="Cancel GRN"
        cancelLabel="Keep GRN"
        onConfirm={handleCancelGrn}
        onCancel={() => setCancelDialogOpen(false)}
      />
    </Box>
  );
}
